import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { companyCommentService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import StackedTable from '../components/StackedTable';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { formatDateTime, formatNumber, plural } from '../utils/format';

// Long notes show their first three lines until opened
const clampedNote = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
};
const isLongNote = (text) => !!text && (text.length > 240 || text.split('\n').length > 3);

const authorOf = (note) => note.createdByName || note.createdByEmail || 'Unknown';

const RecentCompanyNotes = () => {
  const ensureToken = useAuthToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('q') || '';
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  // Ids of long notes opened in full
  const [openNotes, setOpenNotes] = useState(() => new Set());

  const fetchRecentNotes = useCallback(async () => {
    try {
      setError(null);
      setNotes(null);
      await ensureToken();
      const response = await companyCommentService.getRecentComments();
      setNotes(response.data);
    } catch (err) {
      console.error(err);
      setError(serverMessage(err, 'Could not load recent notes.'));
    }
  }, [ensureToken]);

  useEffect(() => {
    fetchRecentNotes();
  }, [fetchRecentNotes]);

  const setFilter = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value); else next.delete('q');
      return next;
    }, { replace: true });
  };

  const toggleNote = (id) => {
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // One box matches the company, the author and the note itself
  const term = filter.trim().toLowerCase();
  const shown = !notes ? [] : !term ? notes : notes.filter((note) => [
    note.companyName, note.companyId, note.createdByName, note.createdByEmail, note.commentText,
  ].some((value) => value && value.toLowerCase().includes(term)));

  const renderBody = () => {
    if (error) return <ErrorState message={error} onRetry={fetchRecentNotes} inCard />;
    if (notes === null) return <LoadingState label="Loading recent notes" />;
    if (notes.length === 0) return <div className="p-4 text-center text-muted">No company notes have been added yet</div>;
    if (shown.length === 0) {
      return (
        <div className="p-4 text-center text-muted">
          No notes match "{filter.trim()}".{' '}
          <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setFilter('')}>Clear filter</Button>
        </div>
      );
    }
    return (
      <StackedTable responsive className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Company</th>
            <th>Note</th>
            <th className="d-none d-md-table-cell">Added by</th>
            <th className="text-nowrap">Added</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((note) => {
            const long = isLongNote(note.commentText);
            const open = openNotes.has(note.id);
            return (
              <tr key={note.id}>
                <td className="align-middle home-notes-company">
                  <Link className="cell-link" to={`/crm/companies/${note.companyId}`}>{note.companyName || note.companyId}</Link>
                </td>
                {/* On the span, as the phone card layout resets white-space on its cells */}
                <td className="align-middle stack-block">
                  <span style={open || !long ? { whiteSpace: 'pre-wrap' } : clampedNote}>{note.commentText}</span>
                  {long && (
                    <Button variant="link" size="sm" className="p-0 d-block" onClick={() => toggleNote(note.id)} aria-expanded={open}>
                      {open ? 'Show less' : 'Show all'}
                    </Button>
                  )}
                </td>
                <td className="align-middle d-none d-md-table-cell text-muted">{authorOf(note)}</td>
                <td className="align-middle text-nowrap">{formatDateTime(note.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </StackedTable>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Recent notes</h1>
          <p className="text-muted">The latest 100 notes across all companies, newest first</p>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Header className="bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
          <Form.Group controlId="recent-notes-filter" style={{ width: '100%', maxWidth: '400px' }}>
            <Form.Label visuallyHidden>Filter notes</Form.Label>
            <Form.Control
              type="search"
              size="sm"
              placeholder="Filter by company, author or text..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={!notes}
            />
          </Form.Group>
          {notes && notes.length > 0 && (
            <span className="small text-muted">
              {term ? `${formatNumber(shown.length)} of ${formatNumber(notes.length)} notes` : plural(notes.length, 'note')}
            </span>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {renderBody()}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RecentCompanyNotes;
